<template>
  <section class="position-relative" id="file-viewer">
    <LmSvgIcon
      v-if="showErrorFeedback"
      class="my-5"
      wrapperClass="d-flex justify-content-center align-items-center flex-column"
      width="6em"
      icon="LmNoDataIcon"
      :text="`Lo sentimos, no pudimos abrir el documento ${flattedFiles[position]?.name}.`"
    />
    <div
      v-show="!showErrorFeedback"
      class="pdf-viewer border rounded"
      :style="`height: ${height}px`"
      ref='viewer'
    ></div>

    <div v-if="showNavigator" class="navigation-buttons">
      <b-button variant="outline-secondary" @click="onBack" :disabled="position === 0">
        <i class="dripicons-chevron-left"></i>
      </b-button>
      <b-button variant="outline-secondary" @click="onForward" :disabled="position === flattedFiles.length - 1">
        <i class="dripicons-chevron-right"></i>
      </b-button>
    </div>

    <aside v-if="openSidebar" class="sidebar">
      <div class="border rounded shadow bg-white">
        <div class="d-flex justify-content-between align-items-center px-4 py-3 border-bottom">
          <i class="dripicons-network-1" style="font-size: 1.5rem"></i>
          <h4 class="text-center m-0">Documentos</h4>
          <span class="clickable" @click="openSidebar = false">
            <i class="mdi mdi-close" style="font-size: 1.5rem"></i>
          </span>
        </div>
        <b-list-group flush class="my-2 mx-2">
          <b-list-group-item
            v-for="(document, documentIndex) of files" :key="documentIndex"
          >
            <span
              class="clickable text-primary text-bold document-name"
              @click="onSetDocument(document); position = documentIndex"
              :class="{ 'text-underline': currentFile === document.path }"
            >
              {{ document.name }}.pdf
            </span>

            <b-list-group class="mt-3" v-if="document.attachment && document.attachments.length">
              <small class="text-muted text-uppercase mb-2">Adjuntos</small>
              <b-list-group-item
                v-for="(attachment, attachmentIndex) of document.attachments" :key="`${documentIndex} + ${attachmentIndex}`"
              >
                <span
                  class="clickable text-primary text-bold document-name"
                  @click="onSetDocument(attachment); position = documentIndex"
                  :class="{ 'text-underline': currentFile === attachment.path }"
                >
                  {{ attachment.name }}
                </span>
              </b-list-group-item>
            </b-list-group>

          </b-list-group-item>
        </b-list-group>
      </div>
    </aside>

    <div id="dynamicSealPlaceholder">
      {{$t('seal_placeholder_help')}}
    </div>
  </section>
</template>

<script>
import WebViewer from '@pdftron/pdfjs-express-viewer'
import LmButton from '@/components/form/elements/LmButton'

export default {
  components: {
    LmButton
  },
  props: {
    files: {
      type: Array,
      default: () => []
    },
    initialPosition: {
      type: Number,
      default: 0
    },
    height: {
      type: Number,
      default: 800
    },
    lazyLoad: {
      type: Boolean,
      default: false
    },
    showNavigator: {
      type: Boolean,
      default: true
    },
    dynamicSealEnabled: {
      type: Boolean,
      default: false
    },
    dynamicSealWidth: {
      type: Number,
      default: 200
    },
    dynamicSealHeight: {
      type: Number,
      default: 125
    }
  },
  data () {
    return {
      sealDynamicCoordinates: [],
      position: 0,
      openSidebar: false,
      instance: null,
      currentFile: this.files[0].path,
      showErrorFeedback: false
    }
  },
  computed: {
    flattedFiles () {
      const files = []
      for (const file of this.files) {
        files.push(file)
        if (file?.attachments?.length) {
          for (const attachment of file.attachments) {
            files.push(attachment)
          }
        }
      }
      return files
    }
  },
  methods: {
    onBack () {
      this.position--
      if (this.lazyLoad) {
        this.$emit('handleBackward', this.flattedFiles[this.position])
      } else {
        this.onSetDocument(this.flattedFiles[this.position])
      }
    },
    onForward () {
      this.position++
      if (this.lazyLoad) {
        this.$emit('handleForward', this.flattedFiles[this.position])
      } else {
        this.onSetDocument(this.flattedFiles[this.position])
      }
    },
    getDownloadFilename (document) {
      const ext = document?.path?.split('.')?.pop()
      const filename = document.name.replace(/\s/g, '_') + '.' + ext
      return filename
    },
    lazyLoadUpdate (doc) {
      this.onSetDocument(doc)
    },
    onSetDocument (document) {
      if (document.path === null) {
        this.currentFile = null
        this.showErrorFeedback = true
      } else {
        this.showErrorFeedback = false

        if (this.instance === null) {
          this.$nextTick(() => {
            const config = {
              path: '/pdfjsexpress',
              initialDoc: document.path,
              filename: this.getDownloadFilename(document),
              licenseKey: process.env?.VUE_APP_PDFTRON_LICENSE || null
            }
            this.createWebViewerInstance(config)
          })
        } else {
          const filename = this.getDownloadFilename(document)
          const path = document.path
          if (this.currentFile !== path) {
            this.currentFile = path
            this.instance.UI.loadDocument(path, {
              filename: filename
            })
          }
        }
      }
    },

    createSealElement (clickedPage, pageCoordinates, zoom) {
      let sealElement = document.createElement('div')
      sealElement.id = `seal_container_${clickedPage}`
      sealElement.className = 'seal_container'
      sealElement.style.position = 'absolute'
      sealElement.style.left = `${pageCoordinates.x * zoom}px`
      sealElement.style.top = `${pageCoordinates.y * zoom}px`
      sealElement.style.width = `${this.dynamicSealWidth * zoom}px`
      sealElement.style.height = `${this.dynamicSealHeight * zoom}px`
      sealElement.style.color = 'red'
      sealElement.style.border = 'dashed'
      sealElement.style.borderWidth = '3px'
      sealElement.style.zIndex = '9999'
      sealElement.style.backgroundColor = 'white'
      sealElement.style.padding = '5px'
      sealElement.style.textAlign = 'center'
      const sealContent = document.getElementById('dynamicSealPlaceholder').innerHTML
      sealElement.innerHTML = sealContent
      const previewButtonId = `seal_preview_button_${clickedPage}`
      const previewButtonHTML = `<button id="${previewButtonId}" class="seal_preview_button" style="margin-top: 5px; background-color: green; border-radius: 5px; color: white; border-color: green;">${this.$t('update_literal')}</button>`
      sealElement.innerHTML += previewButtonHTML

      const deleteButtonId = `seal_delete_button_${clickedPage}`
      const deleteButtonHTML = `<button id="${deleteButtonId}" data-id="${clickedPage}" class="seal_delete_button" style="margin-top: 5px; margin-left: 5px; background-color: red; border-radius: 5px; color: white; border-color: red;">${this.$t('delete')}</button>`
      sealElement.innerHTML += deleteButtonHTML
      return sealElement
    },

    reRenderSealElement (sealDynamicCoordinates, pageNumber, zoom) {
      const sealElement = sealDynamicCoordinates[pageNumber - 1]
      sealElement.element.style.left = sealElement.left * zoom
      sealElement.element.style.top = sealElement.top * zoom
      sealElement.element.style.width = this.dynamicSealWidth * zoom
      sealElement.element.style.height = this.dynamicSealHeight * zoom
      return sealElement.element
    },

    removeSealPreviewContainers () {
      const contenedor = this.$el.querySelector('.pdf-viewer')
      const iframe = contenedor.querySelector('iframe')
      if (iframe) {
        const iframeDocument = iframe.contentDocument || iframe.contentWindow.document
        const sealsContainers = iframeDocument.getElementsByClassName('seal_container')
        for (let sealContainer of sealsContainers) {
          sealContainer.remove()
        }
      }
      this.sealDynamicCoordinates = []
      this.$emit('onSealDynamicCoordinates', this.sealDynamicCoordinates)
    },

    createWebViewerInstance (config) {
      const self = this
      WebViewer(config, this.$refs.viewer)
        .then((instance) => {
          if (this.dynamicSealEnabled) {
            const { documentViewer } = instance.Core
            const getMouseLocation = e => {
              const scrollElement = documentViewer.getScrollViewElement()
              const scrollLeft = scrollElement.scrollLeft || 0
              const scrollTop = scrollElement.scrollTop || 0
              return {
                x: e.pageX + scrollLeft,
                y: e.pageY + scrollTop
              }
            }
            // Prueba de concepto para poder elegir donde posicionar un codigo qr sobre un
            // documento pdf.
            documentViewer.on('dblClick', e => {
            // refer to getMouseLocation implementation above
              const windowCoordinates = getMouseLocation(e)
              const displayMode = documentViewer.getDisplayModeManager().getDisplayMode()
              const page = displayMode.getSelectedPages(windowCoordinates, windowCoordinates)
              const clickedPage = (page.first !== null) ? page.first : documentViewer.getCurrentPage() - 1
              const pageCoordinates = displayMode.windowToPage(windowCoordinates, clickedPage)
              const doc = documentViewer.getDocument()
              const pdfCoords = doc.getPDFCoordinates(pageCoordinates.pageNumber, pageCoordinates.x, pageCoordinates.y)
              const zoom = documentViewer.getZoomLevel()

              const sealElement = this.createSealElement(clickedPage, pageCoordinates, zoom)

              const contenedor = this.$el.querySelector('.pdf-viewer')
              const iframe = contenedor.querySelector('iframe')
              if (iframe) {
                const iframeDocument = iframe.contentDocument || iframe.contentWindow.document
                const pageContainer = iframeDocument.getElementById('pageContainer' + clickedPage)
                const oldElement = iframeDocument.getElementById('seal_container_' + clickedPage)
                if (oldElement && oldElement.parentNode) {
                  oldElement.parentNode.removeChild(oldElement)
                }
                pageContainer.appendChild(sealElement)

                const updateButtons = iframeDocument.getElementsByClassName('seal_preview_button')
                for (let updateButton of updateButtons) {
                  updateButton.addEventListener('click', () => {
                    this.$emit('onReloadPreview')
                  })
                }

                const deleteButtons = iframeDocument.getElementsByClassName('seal_delete_button')
                for (let deleteButton of deleteButtons) {
                  deleteButton.addEventListener('click', () => {
                    const sealIdToDelete = deleteButton.getAttribute('data-id')
                    self.sealDynamicCoordinates[sealIdToDelete - 1] = null
                    this.$emit('onSealDynamicCoordinates', self.sealDynamicCoordinates)
                    const sealElementToDelete = iframeDocument.getElementById(`seal_container_${sealIdToDelete}`)
                    if (sealElementToDelete) {
                      sealElementToDelete.remove()
                    }
                  })
                }
              }
              if (!self.sealDynamicCoordinates[clickedPage - 1]) {
                self.sealDynamicCoordinates[clickedPage - 1] = {}
              }
              // save left and top so we can scale them when the zoom changes
              self.sealDynamicCoordinates[clickedPage - 1] = {
                element: sealElement,
                left: pageCoordinates.x,
                top: pageCoordinates.y,
                // Coordenadas reales en el documento PDF
                pdfCoords: pdfCoords
              }
              this.$emit('onSealDynamicCoordinates', self.sealDynamicCoordinates)
            })
            documentViewer.on('pageComplete', pageNumber => {
              if (self.sealDynamicCoordinates[pageNumber - 1]) {
                const zoom = documentViewer.getZoomLevel()
                let pageContainer
                const contenedor = this.$el.querySelector('.pdf-viewer')
                const iframe = contenedor.querySelector('iframe')
                if (iframe) {
                  const iframeDocument = iframe.contentDocument || iframe.contentWindow.document
                  pageContainer = iframeDocument.getElementById('pageContainer' + pageNumber)
                  if (self.sealDynamicCoordinates[pageNumber - 1]) {
                    const sealElement = this.reRenderSealElement(self.sealDynamicCoordinates, pageNumber, zoom)
                    pageContainer.appendChild(sealElement)
                  }
                }
              }
            })
          }
          // Ajusto la toolbar, no se ve la forma mas linda de hacerlo
          instance.UI.setHeaderItems((header) => {
            let items = header.getItems()
            // console.log('items', items)
            const left = [{
              type: 'actionButton',
              title: 'component.leftPanel',
              img: 'icon-header-sidebar-line',
              onClick: () => {
                this.openSidebar = !this.openSidebar
              }
            }]
            const zoomControls = items.at(3)
            zoomControls.hiddenOnMobileDevice = false

            const right = [
              { type: 'spacer' },
              items.at(2), // view control
              zoomControls,
              items.at(9), // search control
              items.at(11) // settings control
            ]
            items = [...left, ...right]
            header.update(items)
          })
          instance.UI.setLanguage('es')

          if (this.dynamicSealEnabled) {
            instance.UI.setMinZoomLevel('90%')
            instance.UI.setMaxZoomLevel('100%')
          }

          instance.UI.useEmbeddedPrint(true)

          this.instance = instance
          const { Core } = instance

          // El documento terminó de cargar
          // Core.documentViewer.addEventListener('documentLoaded', () => {
          //   console.log('document loaded')
          // })

          // Deshabilito las anotaciones, no tenemos forma de guardarlas por el momento
          Core.annotationManager.enableReadOnlyMode()

          // Horrible forma provisoria para ocultar el "Firme aquí"
          Core.Annotations.SignatureWidgetAnnotation.prototype.createSignHereElement = () => {
            const div = document.createElement('div')
            return div
          }
        })
    }
  },
  watch: {
    // position (val) {
    //   if (this.instance) {
    //     this.onSetDocument(this.flattedFiles[val].path)
    //   }
    // },
    initialPosition (val) {
      if (this.flattedFiles[val]) {
        this.position = val
      }
    }
  },
  mounted () {
    if (this.initialPosition) {
      this.position = this.initialPosition
    }

    if (this.flattedFiles[this.initialPosition].path === null) {
      this.showErrorFeedback = true
    }

    // Actualizo la version de pdfjs-express-viewer en base a este issue
    // https://pdfjs.community/t/pdf-viewer-react-sample-has-error/1927

    const config = {
      path: '/pdfjsexpress',
      licenseKey: process.env?.VUE_APP_PDFTRON_LICENSE || null
    }

    if (this.flattedFiles[this.initialPosition].path) {
      config.initialDoc = this.flattedFiles[this.initialPosition].path
      config.filename = this.getDownloadFilename(this.flattedFiles[this.initialPosition])
    }

    this.createWebViewerInstance(config)
  },
  beforeDestroy () {
    if (this.$refs.viewer) {
      this.$refs.viewer.remove()
    }
    if (this.instance) {
      this.instance.dispose()
    }
  }
}
</script>

<style lang="sass">
  #file-viewer
    .pdf-viewer
      box-shadow: rgba(100, 100, 111, 0.2) 0px 7px 29px 0px !important

    .document-name
      word-break: break-all

    .navigation-buttons
      position: absolute
      top: 50%
      transform: translateY(-50%)
      width: 100%
      display: flex
      justify-content: space-between
      padding: 0 1rem
      > button > i
        display: block
        margin-bottom: 2px

    .sidebar
      position: absolute
      top: 3rem
      left: 1rem
      background: white
      width: 300px
      .text-underline
        text-decoration: underline

    #dynamicSealPlaceholder
      display: none
</style>
